"use client";

/**
 * Text and Button Renderer
 * Renders text + button component preview in the canvas
 */

import { useMemo } from "react";
import type React from "react";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { CtaButton, TextButtonComponent } from "../../../_types";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartButton } from "../../public/SmartButton";
import { EmailLinkIndicator } from "./_shared/EmailLinkIndicator";
import { InlineTextEdit } from "./_shared/InlineTextEdit";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

interface TextButtonRendererProps {
  component: TextButtonComponent;
}

/**
 * Renders a text and button section preview
 */
export function TextButtonRenderer({ component }: TextButtonRendererProps): React.ReactElement {
  const { title, description } = component;
  const { primaryColor, eventId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();

  // Derived values — resolve ctaButtons with backward compat for legacy buttonText/buttonLink
  const buttons: CtaButton[] = useMemo(() => {
    if (component.ctaButtons && component.ctaButtons.length > 0) return component.ctaButtons;
    if (component.buttonText) {
      return [{ id: "legacy", label: component.buttonText, url: component.buttonLink || "" }];
    }
    return [];
  }, [component.ctaButtons, component.buttonText, component.buttonLink]);

  // Render
  return (
    <SectionContainer background={component.background}>
      <div className="flex flex-col items-center gap-6">
        {(title || !isPublic) && (
          <InlineTextEdit
            tag="h2"
            value={title || ""}
            placeholder="Click to add title..."
            className="text-2xl font-bold text-[color:var(--lp-text,#111827)] md:text-3xl"
            componentId={component.id}
            fieldId="textbutton-title"
            field="title"
          />
        )}

        <div className="self-stretch">
          <InlineTipTapEdit
            content={description || ""}
            className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
            componentId={component.id}
            fieldId="textbutton-content"
            field="description"
            placeholder="Click to add description..."
          />
        </div>

        {(component.showButton ?? true) && buttons.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3">
            {buttons.map((cta) => {
              const bgColor = cta.color ?? primaryColor;
              const textColor = getButtonTextColor(bgColor);
              return isPublic && cta.url ? (
                <SmartButton
                  key={cta.id}
                  href={cta.url}
                  label={cta.label || "Button"}
                  primaryColor={bgColor}
                  eventId={eventId}
                  buttonId={cta.id}
                  linkedSteps={cta.linkedSteps}
                  className="inline-flex items-center rounded-xl px-6 py-3 font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
                  style={{ backgroundColor: bgColor, color: textColor }}
                />
              ) : (
                <span
                  key={cta.id}
                  className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold"
                  style={{ backgroundColor: bgColor, color: textColor }}
                >
                  <EmailLinkIndicator href={cta.url} />
                  {cta.label || "Button"}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </SectionContainer>
  );
}
